/**
 * QuoteAcceptPage — PUBLIC, no auth required.
 * Route: /quote/:token
 *
 * Customer-facing pricing quote. Fetches GET /billing/quote/:token from the messaging API
 * (no auth header). Customer can review line items and click Accept.
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const MESSAGING_API = (import.meta.env.VITE_MESSAGING_API_URL as string) ?? "";

interface QuoteLineItem {
  description: string;
  quantity: number;
  unitDollars: number;
  totalDollars: number;
}

interface QuoteData {
  invoiceNumber: string;
  currency: string;
  totalAmount: number;
  totalDollars: number;
  invoiceDate: string;
  notes?: string;
  alreadyAccepted: boolean;
  lineItems: QuoteLineItem[];
}

function fmtCurrency(val: number) {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDate(d?: string) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function Confetti() {
  const colors = ["#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#8b5cf6"];
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-40">
      {Array.from({ length: 50 }, (_, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${Math.random() * 100}%`,
            top: "-20px",
            width: 8 + Math.random() * 8,
            height: 8 + Math.random() * 8,
            backgroundColor: colors[i % colors.length],
            borderRadius: i % 3 === 0 ? "50%" : "2px",
            animation: `confetti-fall ${2 + Math.random() * 1.5}s ${Math.random() * 1.5}s ease-in forwards`,
          }}
        />
      ))}
      <style>{`@keyframes confetti-fall { to { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
    </div>
  );
}

export default function QuoteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [signedBy, setSignedBy] = useState("");
  const [signedEmail, setSignedEmail] = useState("");

  useEffect(() => {
    if (!token) return;
    fetch(`${MESSAGING_API}/billing/quote/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "Quote not found or no longer available." : "Failed to load quote.");
        return r.json();
      })
      .then((data: QuoteData) => {
        setQuote(data);
        if (data.alreadyAccepted) setAccepted(true);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      const r = await fetch(`${MESSAGING_API}/billing/quote/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signedByName: signedBy || null, signedByEmail: signedEmail || null }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? "Failed to accept quote.");
      }
      setAccepted(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 text-sm animate-pulse">Loading quote…</div>
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">📄</div>
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Quote Not Available</h2>
          <p className="text-slate-500 text-sm">{error}</p>
          <p className="text-slate-400 text-xs mt-3">
            If you believe this is an error, contact{" "}
            <a href="mailto:billing@velaro.com" className="underline text-blue-600">billing@velaro.com</a>.
          </p>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Confetti />
        <div className="bg-white rounded-xl shadow p-10 max-w-md w-full text-center z-10 relative">
          <div className="text-5xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-emerald-700 mb-2">Quote Accepted!</h2>
          <p className="text-slate-600 text-sm mb-1">
            Thank you for accepting quote <strong>{quote?.invoiceNumber}</strong>.
          </p>
          <p className="text-slate-500 text-sm">
            Your account manager will be in touch shortly to complete onboarding.
          </p>
          <p className="text-slate-400 text-xs mt-4">
            Questions?{" "}
            <a href="mailto:billing@velaro.com" className="underline text-blue-600">billing@velaro.com</a>
          </p>
        </div>
      </div>
    );
  }

  if (!quote) return null;

  // Separate line items by type for display
  const baseItems = quote.lineItems.filter(
    (l) => !l.description.toLowerCase().includes("credit card") && !l.description.toLowerCase().includes("sales tax")
  );
  const ccFeeItem = quote.lineItems.find((l) => l.description.toLowerCase().includes("credit card"));
  const taxItem = quote.lineItems.find((l) => l.description.toLowerCase().includes("sales tax"));
  const subtotal = baseItems.reduce((s, l) => s + l.totalDollars, 0);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
            Pricing Quote
          </div>
          <h1 className="text-2xl font-bold text-slate-900">{quote.invoiceNumber}</h1>
          <p className="text-slate-500 text-sm mt-1">Prepared {fmtDate(quote.invoiceDate)}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Line items */}
          <div className="p-6 border-b border-slate-100">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
              Summary
            </h3>
            <div className="space-y-3">
              {baseItems.map((item, i) => (
                <div key={i} className="flex justify-between items-start gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{item.description}</p>
                    {item.quantity !== 1 && (
                      <p className="text-xs text-slate-400">
                        {item.quantity} × {fmtCurrency(item.unitDollars)}
                      </p>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">
                    {fmtCurrency(item.totalDollars)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Subtotal / fees / tax / total */}
          <div className="px-6 py-4 bg-slate-50 space-y-2">
            <div className="flex justify-between text-sm text-slate-600">
              <span>Subtotal</span>
              <span>{fmtCurrency(subtotal)}</span>
            </div>
            {ccFeeItem && (
              <div className="flex justify-between text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <span>💳</span> {ccFeeItem.description}
                </span>
                <span>{fmtCurrency(ccFeeItem.totalDollars)}</span>
              </div>
            )}
            {taxItem && (
              <div className="flex justify-between text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <span>🏛</span> {taxItem.description}
                </span>
                <span>{fmtCurrency(taxItem.totalDollars)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold text-slate-900 pt-2 border-t border-slate-200">
              <span>Total</span>
              <span>{fmtCurrency(quote.totalDollars)}</span>
            </div>
          </div>

          {/* Notes */}
          {quote.notes && (
            <div className="px-6 py-4 border-t border-slate-100">
              <p className="text-xs text-slate-500 whitespace-pre-wrap">{quote.notes}</p>
            </div>
          )}

          {/* Acceptance form */}
          <div className="p-6 border-t border-slate-100">
            <p className="text-sm text-slate-600 mb-4">
              By clicking <strong>Accept Quote</strong> you agree to the pricing above. Your name and email
              are optional but help us process your acceptance faster.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={signedEmail}
                onChange={(e) => setSignedEmail(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {error && (
              <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3 mb-3">{error}</div>
            )}
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
            >
              {accepting ? "Accepting…" : `Accept Quote — ${fmtCurrency(quote.totalDollars)}`}
            </button>
            <p className="text-xs text-slate-400 text-center mt-3">
              This quote does not automatically charge your card. Payment will be collected separately.
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Velaro · Questions?{" "}
          <a href="mailto:billing@velaro.com" className="underline">billing@velaro.com</a>
        </p>
      </div>
    </div>
  );
}
